import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { Link } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useQuery } from "@tanstack/react-query";
import {
  getSnowflakeJobFilters,
  getSnowflakeJobGraphData,
  getSnowflakeJobs,
  getDbtHealthChecks,
  getSnowflakeJobGraphSummary,
} from "@/helpers/apis";
import { GetSnowflakeJobItem, WorkflowType } from "@/pages/query/types";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import classNames from "classnames";
import { RelativeComponentLoader } from "@/components/loader";
import { Card, NativeSelect, Stack, Typography } from "@altimateai/lego";
import { Chart, ChartTypes } from "@uicore";
import { DbtIcon } from "@assets/icons";
import { DateRange } from "@/components/dateRange";
import dayjs from "dayjs";
import { Bar, Rectangle, TooltipProps, Line } from "recharts";
import { FilterTagWrapper } from "@/components/tags";
import { getFilterStringByMode } from "@/components/tags/utils";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { CostChartTooltip } from "../Graph";
import { createColorMapFromObjectKeys, getPercentKey, OTHER } from "@/pages/query/common";
import { buildDbtLabel } from "./useDbtChartData";
import { GET_DBT_FILTERS, GET_DBT_GRAPH_DATA, GET_DBT_JOBS_LIST } from "@/pages/query/constants";
import { FilterType, TableHeaderType, parseSortDirection } from "@/components/tables/types";
import { TotalCostHeader } from "@/pages/query/common/TotalCostHeader";
import { Spinner } from "@altimateai/lego";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { FilterTextChangeValue } from "@/components/filterDropdown";

export const DbtJobList: React.FC = () => {
  const { searchParams, deleteSearchParamsByKeyValue, updateSearchParamsByKey } =
    useFiltersBySearchParams();
  const { setPage, setPageSize, totalPages, totalItems, setTotalPages, setTotalItems } =
    useTablePagination();

  const { currency, currencySymbol, enableDbtJobsListing } = useAppState();
  const defaultTab = enableDbtJobsListing ? "jobs" : "models";

  const sortAttribute = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");

  const costFilter = searchParams.get("query_cost");
  const executionTimeFilter = searchParams.get("executionTime");
  const workflowFilter = searchParams.getAll("workflows") || [];
  const warehouseFilter = searchParams.getAll("warehouses") || [];
  const teamsFilter = searchParams.getAll("teams") || [];
  const appsFilter = searchParams.getAll("apps") || [];
  const integrationNameFilter = searchParams.getAll("integration_names") || [];
  const integrationEnvironmentFilter = searchParams.getAll("integration_environments") || [];

  const executionTimeFilterMode = searchParams.get("executionTimeFilterMode") || "gt";
  const costFilterMode = searchParams.get("costFilterMode") || "gt";

  // Read pagination from URL directly for query to avoid timing issues with context sync
  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "20", 10);

  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);
  const [labelMap, setLabelMap] = useState<Record<string, string>>({});
  const [chartType, setChartType] = useState<string>("cost");

  const { startDate, endDate, onUpdate } = useDateFromSearchParams();

  // Query parameters
  const getFilterParams = () => {
    const params: Record<string, unknown> = {
      page: page + 1,
      size: pageSize,
      sortAttribute,
      sortOrder,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      workflows: workflowFilter,
      warehouses: warehouseFilter,
      teams: teamsFilter,
      apps: appsFilter,
      integration_names: integrationNameFilter,
      integration_environments: integrationEnvironmentFilter,
      workflow_type: WorkflowType.DBT,
    };

    if (costFilter) {
      params.costFilter = costFilter;
      params.costFilterMode = costFilterMode === "lt" ? "lt" : "gt";
    }

    if (executionTimeFilter) {
      params.executionTimeFilter = parseFloat(executionTimeFilter) * 1000;
      params.executionTimeFilterMode = executionTimeFilterMode === "lt" ? "lt" : "gt";
    }

    return params;
  };

  // Fetch job data using OpenSearch query
  const { data, isLoading: isTableDataLoading } = useQuery({
    queryKey: [
      GET_DBT_JOBS_LIST,
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      costFilter,
      costFilterMode,
      ...workflowFilter,
      ...warehouseFilter,
      ...teamsFilter,
      ...appsFilter,
      ...integrationNameFilter,
      ...integrationEnvironmentFilter,
      executionTimeFilter,
      executionTimeFilterMode,
      startDate,
      endDate,
    ],
    queryFn: () => getSnowflakeJobs(getFilterParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      setTotalItems(data?.total || 0);
      setTotalPages(data?.pages || 0);
    }
  }, [data]);

  const { data: filters, isLoading: isFiltersLoading } = useQuery({
    queryKey: [GET_DBT_JOBS_LIST, GET_DBT_FILTERS, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobFilters({
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: WorkflowType.DBT,
      }),
  });

  const graphParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
    workflows: workflowFilter,
    warehouses: warehouseFilter,
    teams: teamsFilter,
    apps: appsFilter,
    integration_names: integrationNameFilter,
    integration_environments: integrationEnvironmentFilter,
    workflow_type: WorkflowType.DBT,
  };

  const graphSummaryQueryKey = [
    GET_DBT_JOBS_LIST,
    "dbt_jobs_graph_summary",
    startDate,
    endDate,
    ...workflowFilter,
    ...warehouseFilter,
    ...teamsFilter,
    ...appsFilter,
    ...integrationNameFilter,
    ...integrationEnvironmentFilter,
  ];

  const { data: graphData, isFetching: isGraphLoading } = useQuery({
    queryKey: [
      GET_DBT_JOBS_LIST,
      GET_DBT_GRAPH_DATA,
      startDate,
      endDate,
      ...workflowFilter,
      ...warehouseFilter,
      ...teamsFilter,
      ...appsFilter,
      ...integrationNameFilter,
      ...integrationEnvironmentFilter,
    ],
    queryFn: () => getSnowflakeJobGraphData(graphParams),
    enabled: chartType === "cost",
  });

  // Handle successful graph data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (!graphData) return;

    const totalCost: Record<string, number> = {};
    const labels: Record<string, string> = {};

    for (const item of graphData) {
      for (const key in item) {
        if (key === "date") continue;

        const nestedObject = item[key];
        if (!nestedObject) continue;

        // Use the workflow_id key directly for unique identification
        labels[key] = buildDbtLabel(key, nestedObject);

        totalCost[key] = totalCost[key] || 0;
        totalCost[key] += nestedObject?.cost_value || 0;
      }
    }

    const totalCostItems = Object.entries(totalCost);
    totalCostItems.sort((a, b) => b[1] - a[1]);

    setLabelMap(labels);
    setSelectedLegends(
      (totalCostItems.length > 10 ? [OTHER] : []).concat(totalCostItems.slice(0, 10).map(x => x[0]))
    );
  }, [graphData]);

  const { data: healthCheckData, isLoading: isHealthCheckLoading } = useQuery({
    queryKey: ["dbt_health_checks", startDate, endDate, chartType],
    queryFn: () =>
      getDbtHealthChecks({
        name: "Low test coverage in dbt models",
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        limit: 100,
      }),
    enabled: chartType === "test_coverage",
  });

  const handleCostFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      query_cost: value,
      costFilterMode: mode,
    });
  };

  const handleExecutionTimeFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      executionTime: value,
      executionTimeFilterMode: mode,
    });
  };

  const handleWarehouseFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ warehouses: value });
  };

  const handleIntegrationNameFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ integration_names: value });
  };

  const handleIntegrationEnvironmentFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ integration_environments: value });
  };

  const integrationEnvironmentOptions = useMemo(() => {
    if (integrationNameFilter.length === 0) return [];
    return (
      filters?.integrations_with_environments
        ?.filter(i => integrationNameFilter.includes(i.name))
        .flatMap(i => i.environments)
        .filter((env, idx, arr) => arr.indexOf(env) === idx)
        .map(env => ({ label: env, value: env })) ?? []
    );
  }, [integrationNameFilter, filters?.integrations_with_environments]);

  // Table headers
  const tableHeaders: TableHeaderType<GetSnowflakeJobItem>[] = [
    {
      id: 1,
      label: "Project Name",
      uid: "dbt_project_name",
      render: (item: GetSnowflakeJobItem) => (
        <Link
          className="al-text-brand al-break-all al-text-left"
          to={`/query/dbt-jobs/${encodeURIComponent(item.workflow_id)}/${defaultTab}?start_date=${startDate?.toISOString()}&end_date=${endDate?.toISOString()}`}
          state={{
            project: item.dbt_project_name,
            integration: item.integration_environment ?? "-",
          }}
        >
          {item.dbt_project_name ?? "-"}
        </Link>
      ),
    },
    {
      id: 2,
      label: "Total Cost",
      uid: "query_cost",
      render: (item: GetSnowflakeJobItem) => (
        // @ts-ignore
        <div>{formatCost(item.query_cost, currency)}</div>
      ),
      sort: {
        sortSearchKey: "sortAttribute",
      },
      filter: {
        id: "query_cost",
        filterType: FilterType.TEXT,
        value: costFilter ?? undefined,
        placeHolder: `Specify cost in ${currencySymbol}`,
        onChange: handleCostFilterChange,
        label: "Total Cost",
        filterModeSearchKey: "costFilterMode",
        filterMode: { value: costFilterMode },
      },
    },
    {
      id: 3,
      label: "# Runs",
      uid: "runs",
      render: (item: GetSnowflakeJobItem) => <div>{item.runs}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 4,
      label: "Test Coverage",
      uid: "test_coverage",
      render: (item: GetSnowflakeJobItem) => (
        <div>{item.test_coverage ? `${item.test_coverage}%` : "-"}</div>
      ),
    },
    {
      id: 5,
      label: "Average Execution Time",
      uid: "average_execution_time",
      render: (item: GetSnowflakeJobItem) => <div>{formatMilliseconds(item.execution_time)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
        sortParamValue: "execution_time",
      },
      filter: {
        id: "executionTime",
        filterType: FilterType.TEXT,
        value: executionTimeFilter ?? undefined,
        placeHolder: "Specify time in seconds",
        onChange: handleExecutionTimeFilterChange,
        label: "Execution Time",
        filterModeSearchKey: "executionTimeFilterMode",
        filterMode: { value: executionTimeFilterMode },
      },
    },
    {
      id: 6,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      render: (item: GetSnowflakeJobItem) => (
        // @ts-ignore
        <div>{formatBytes(item.bytes_scanned)}</div>
      ),
    },
    {
      id: 7,
      label: "Warehouses",
      uid: "warehouses",
      render: (item: GetSnowflakeJobItem) => (
        <div className={classNames("al-break-words", "lines-2")}>
          {item.warehouse_names.join(", ")}
        </div>
      ),
      filter: {
        id: "warehouses",
        label: "Warehouses",
        searchKey: "warehouses",
        filterType: FilterType.DROPDOWN,
        value: warehouseFilter,
        popoverContentProps: {
          avoidCollisions: true,
        },
        options:
          filters?.warehouses.map(t => ({
            label: t,
            value: t,
          })) ?? [],
        onChange: handleWarehouseFilterChange,
      },
    },
    {
      id: 8,
      label: "Integration",
      uid: "integration_name",
      render: (item: GetSnowflakeJobItem) => <span>{item.integration_name ?? "-"}</span>,
      filter: {
        id: "integration_names",
        label: "Integration",
        searchKey: "integration_names",
        filterType: FilterType.DROPDOWN,
        value: integrationNameFilter,
        popoverContentProps: {
          avoidCollisions: true,
        },
        options:
          filters?.integration_names?.map(name => ({
            label: name,
            value: name,
          })) ?? [],
        onChange: handleIntegrationNameFilterChange,
      },
    },
    {
      id: 9,
      label: "Integration Environment",
      uid: "integration_environment",
      render: (item: GetSnowflakeJobItem) => <span>{item.integration_environment ?? "-"}</span>,
      filter: {
        id: "integration_environments",
        label: "Integration Environment",
        searchKey: "integration_environments",
        filterType: FilterType.DROPDOWN,
        value: integrationEnvironmentFilter,
        popoverContentProps: {
          avoidCollisions: true,
        },
        options: integrationEnvironmentOptions,
        onChange: handleIntegrationEnvironmentFilterChange,
      },
    },
  ];

  const chartData = useMemo(() => {
    if (chartType === "cost") {
      if (!graphData) {
        return [];
      }

      const result = [];
      for (const item of graphData) {
        const resultItem: { date?: string } & Record<string, number> = {};
        resultItem["date"] = item.date;

        for (const key in item) {
          if (key === "date") {
            continue;
          }

          const nestedObject = item[key];
          if (!nestedObject) {
            continue;
          }

          // Use the workflow_id key directly as the unique data key
          // to avoid collisions when multiple integrations share a project name
          resultItem[key] = nestedObject.cost_value || 0;

          if (result.length > 0) {
            const lastItem = result[result.length - 1];
            if (lastItem[key] > 0) {
              resultItem[getPercentKey(key)] =
                (nestedObject.cost_value - lastItem[key]) / lastItem[key];
            }
          }
        }

        result.push(resultItem);
      }
      return result;
    } else if (chartType === "test_coverage") {
      if (!healthCheckData?.results || healthCheckData.results.length === 0) {
        return [];
      }

      // Group data by project and create one line per project
      const projectDataMap = new Map<string, Array<{ date: string; coverage: number }>>();

      healthCheckData.results.forEach(result => {
        const testCoverageInsight = result.project_insights.find(
          insight => insight.name === "Low test coverage in dbt models"
        );

        if (testCoverageInsight) {
          const projectName = result.dbt_project_name;
          const coverage = testCoverageInsight.insight_metadata?.coverage ?? 0;
          const formattedDate = dayjs(result.run_timestamp).format("MMM-DD");

          if (!projectDataMap.has(projectName)) {
            projectDataMap.set(projectName, []);
          }
          projectDataMap.get(projectName)!.push({ date: formattedDate, coverage });
        }
      });

      // Transform into chart data format with one column per project
      const dateMap = new Map<string, Record<string, number>>();

      projectDataMap.forEach((dataPoints, projectName) => {
        dataPoints.forEach(({ date, coverage }) => {
          if (!dateMap.has(date)) {
            dateMap.set(date, {});
          }
          dateMap.get(date)![projectName] = coverage;
        });
      });

      // Convert to array and sort by date
      const chartData = Array.from(dateMap.entries()).map(([date, projects]) => ({
        date,
        ...projects,
      }));

      chartData.sort((a, b) => dayjs(a.date, "MMM-DD").diff(dayjs(b.date, "MMM-DD")));

      return chartData;
    }

    return [];
  }, [chartType, graphData, healthCheckData]);

  const graphDataSubset = chartData;

  const colorsMap = useMemo(() => {
    return createColorMapFromObjectKeys(graphData || [], {
      reservedMappings: { [OTHER]: "#5B9FFF" },
      excludeKeys: ["date"],
      nestedKeyExtractor: key => key,
    });
  }, [graphData]);

  // Get unique project names for test coverage chart
  const projectNames = useMemo(() => {
    if (chartType !== "test_coverage" || !chartData || chartData.length === 0) {
      return [];
    }
    const projects = new Set<string>();
    chartData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== "date") {
          projects.add(key);
        }
      });
    });
    return Array.from(projects);
  }, [chartType, chartData]);

  // Colors for test coverage lines
  const coverageColors = [
    "#247EFE",
    "#FF6B6B",
    "#4ECDC4",
    "#FFD93D",
    "#6C5CE7",
    "#A29BFE",
    "#FD79A8",
    "#00B894",
  ];

  if (isFiltersLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  return (
    <Card className="al-p-4">
      <Stack direction="column">
        <Stack align="center">
          <Stack align={"center"} gap={"md"}>
            <DbtIcon className={"al-h-4 al-w-4"} />
            <Typography variant="page-heading">Dbt Projects</Typography>
          </Stack>
          <div className="spacer" />
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
            onDateRangeSelect={onUpdate}
          />
        </Stack>
        <Stack direction="column">
          <Stack direction={"column"} className="al-border al-rounded-md al-pb-8 al-px-4 al-pt-4">
            <Stack justify={"between"} align={"center"}>
              <TotalCostHeader
                queryKey={graphSummaryQueryKey}
                queryFn={getSnowflakeJobGraphSummary}
                params={graphParams}
                title={"Total Jobs Cost"}
              />
              <Stack align={"center"}>
                <Typography size="regular">Chart of: </Typography>
                <NativeSelect
                  variant="outline"
                  id="chart-selector"
                  onChange={(value: unknown) => setChartType(value as string)}
                  value={chartType ?? ""}
                  options={[
                    { label: "Cost", value: "cost" },
                    { label: "Test Coverage", value: "test_coverage" },
                  ]}
                />
              </Stack>
            </Stack>
            {isGraphLoading || isHealthCheckLoading ? (
              <Stack align="center" justify="center" className="al-h-80">
                <Spinner /> Loading...
              </Stack>
            ) : graphDataSubset.length > 0 ? (
              <Chart
                title={chartType === "cost" ? "Cost" : "Test Coverage"}
                xAxisDataKey="date"
                yAxisLabel={{ value: chartType === "cost" ? "Cost" : "Test Coverage (%)" }}
                xAxisLabelFormatter={value =>
                  chartType === "cost" ? dayjs(value).format("MMM-DD") : value
                }
                yAxisLabelFormatter={value =>
                  chartType === "cost" ? formatCost(value, currency) : `${value}%`
                }
                type={chartType === "cost" ? ChartTypes.BarChart : ChartTypes.LineChart}
                data={graphDataSubset}
                hideFullscreenButton
                tooltipProps={
                  chartType === "cost"
                    ? {
                        content: (props: TooltipProps<number, string>) => (
                          <CostChartTooltip {...props} />
                        ),
                      }
                    : {}
                }
                chartProps={{}}
              >
                {({ barSize }) => {
                  if (chartType === "cost") {
                    return (
                      <>
                        {selectedLegends?.map((d, i) => (
                          <Bar
                            key={d}
                            barSize={barSize}
                            dataKey={d}
                            name={labelMap[d] || d}
                            fill={colorsMap[d]}
                            stackId="single"
                            shape={(props: unknown) => {
                              (props as { radius: number[] }).radius =
                                i === selectedLegends.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0];
                              return <Rectangle {...(props as Record<string, unknown>)} />;
                            }}
                          />
                        ))}
                      </>
                    );
                  } else {
                    return (
                      <>
                        {projectNames.map((projectName, index) => (
                          <Line
                            key={projectName}
                            type="monotone"
                            dataKey={projectName}
                            stroke={coverageColors[index % coverageColors.length]}
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            name={projectName}
                          />
                        ))}
                      </>
                    );
                  }
                }}
              </Chart>
            ) : (
              <Stack align="center" justify="center" className="al-h-80">
                No data is present
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack className={"al-mt-4"} direction="column">
          <Stack align="center">
            <div className="fw-semibold fs-5">Projects List</div>
            <div className="spacer" />
          </Stack>
          <FilterTagWrapper
            applicableFor={undefined}
            filters={{
              costFilter: {
                label: "Cost",
                filterStr: getFilterStringByMode(costFilter, costFilterMode) || [],
                searchKey: "cost",
                onclose: () =>
                  deleteSearchParamsByKeyValue({
                    query_cost: undefined,
                    costFilterMode: undefined,
                  }),
              },
              executionTimeFilter: {
                label: "Execution time",
                filterStr:
                  getFilterStringByMode(executionTimeFilter, executionTimeFilterMode) || [],
                searchKey: "executionTime",
                onclose: () =>
                  deleteSearchParamsByKeyValue({
                    executionTime: undefined,
                    executionTimeFilterMode: undefined,
                  }),
              },
              warehouseFilter: {
                label: "Warehouses",
                filterStr: warehouseFilter.join(", "),
                searchKey: "warehouses",
                onclose: () => deleteSearchParamsByKeyValue({ warehouses: undefined }),
              },
              workflowFilter: {
                label: "Workload",
                filterStr: workflowFilter.join(", "),
                searchKey: "workflows",
                onclose: () => deleteSearchParamsByKeyValue({ workflows: undefined }),
              },
              teamsFilter: {
                label: "Projects",
                filterStr: teamsFilter.join(", "),
                searchKey: "teams",
                onclose: () => deleteSearchParamsByKeyValue({ teams: undefined }),
              },
              appsFilter: {
                label: "Apps",
                filterStr: appsFilter.join(", "),
                searchKey: "apps",
                onclose: () => deleteSearchParamsByKeyValue({ apps: undefined }),
              },
              integrationNameFilter: {
                label: "Integration",
                filterStr: integrationNameFilter.join(", "),
                searchKey: "integration_names",
                onclose: () => deleteSearchParamsByKeyValue({ integration_names: undefined }),
              },
              integrationEnvironmentFilter: {
                label: "Integration Environment",
                filterStr: integrationEnvironmentFilter.join(", "),
                searchKey: "integration_environments",
                onclose: () =>
                  deleteSearchParamsByKeyValue({ integration_environments: undefined }),
              },
            }}
          />
          {isTableDataLoading ? (
            <Stack align="center" justify="center" className="al-h-80">
              <Spinner /> Loading...
            </Stack>
          ) : (
            <DataTable
              onRowClick={() => {}}
              resetPage={() => {}}
              data={{ items: data?.items || [] }}
              header={tableHeaders}
            />
          )}
          <Paginate
            isLoading={isTableDataLoading}
            itemCount={totalItems}
            numPages={totalPages}
            page={page}
            pageSize={pageSize}
            onPageClick={setPage}
            onPageSizeChange={setPageSize}
            showPageSize
          />
        </Stack>
      </Stack>
    </Card>
  );
};
